import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { fetchUserById, fetchAllEmployees } from "../../store/slices/userSlice";

import Breadcrumb from "../../components/navigation/Bredcrumbs";
import DetailTopPart from "../../components/popups/DetailTopPart";
import EmployeeListTable from "../../components/tables/EmployeListTable";
import FilterTopBar from "../../components/navigation/FilterTopBar";

import user_img_1 from "../../assets/images/user_img_1.png";
import plus_icon from "../../assets/images/plus_icon.svg";
import filter_list_icon from "../../assets/images/filter_list_icon.svg";

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Unique keys for this specific company page instance
  const DETAIL_KEY = `companyDetail_${id}`;
  const EMPLOYEE_LIST_KEY = `companyEmployeeList_${id}`;

  const selectedUser = useAppSelector((s) => s.user.entityByKey[DETAIL_KEY] || null);
  const loadingSelectedUser = useAppSelector((s) => s.user.entityLoadingByKey[DETAIL_KEY] || false);

  const employeeList = useAppSelector((s) => s.user.listDataByKey[EMPLOYEE_LIST_KEY] || []);
  const loadingEmployees = useAppSelector((s) => s.user.listLoadingByKey[EMPLOYEE_LIST_KEY] || false);
  const totalRecords = useAppSelector((s) => s.user.totalByKey[EMPLOYEE_LIST_KEY] || 0);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (id) {
      dispatch(fetchUserById({ id, key: DETAIL_KEY }));
    }
  }, [id, dispatch, DETAIL_KEY]);

  useEffect(() => {
    if (id) {
      const timeout = setTimeout(() => {
        dispatch(
          fetchAllEmployees({
            params: {
              companyId: id,
              pageIndex: page - 1,
              pageSize: pageSize,
              search: search || undefined,
            },
            key: EMPLOYEE_LIST_KEY,
          })
        );
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [id, dispatch, search, page, EMPLOYEE_LIST_KEY]);

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const companyData = useMemo(() => {
    if (!selectedUser) return null;
    return {
      name: selectedUser.name || selectedUser.companyName || "N/A",
      email: selectedUser.email || "N/A",
      phone: selectedUser.phone || "N/A",
      address: selectedUser.address || "N/A",
      image: selectedUser.profile
        ? BASE_URL + selectedUser.profile
        : selectedUser.logo
          ? BASE_URL + selectedUser.logo
          : user_img_1,
    };
  }, [selectedUser, BASE_URL]);

  const breadcrumbItems = [
    { label: "Companies", link: "/companies" },
    { label: companyData?.name || "Company Detail", active: true },
  ];

  return (
    <div className="company_detail">
      <Breadcrumb items={breadcrumbItems} />

      {loadingSelectedUser && !companyData ? (
        <div className="p-4 text-center">Loading Company Details</div>
      ) : companyData ? (
        <DetailTopPart
          client={{
            name: companyData.name,
            email: companyData.email,
            phone: companyData.phone,
            nextPayrollDate: companyData.address,
            image: companyData.image,
          }}
          labels={{
            email: "Email",
            phone: "Contact",
            nextPayrollDate: "Address",
          }}
          buttons={{
            addEmployee: {
              text: "Add Employee",
              icon: plus_icon,
              className: "btn btn_secondary mr_10 ms-auto",
              onClick: () => navigate("/addnewemployee", { state: { companyId: id } }),
            },
            payroll: {
              text: "Payroll",
              icon: filter_list_icon,
              className: "btn btn_primary",
              onClick: () => navigate(`/payroll/${id}`),
            },
          }}
          className="compnay_detail_top"
        />
      ) : (
        <div className="p-4 text-center">Company Not Found</div>
      )}

      <div className="employee_table">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="white_box px_20 py_20 mt_40 border-0">
                <FilterTopBar
                  title="Employees"
                  count={totalRecords}
                  search={search}
                  onSearchChange={(val) => {
                    setSearch(val);
                    setPage(1);
                  }}
                  placeholder="Search Employees"
                />
                <EmployeeListTable
                  data={employeeList}
                  loading={loadingEmployees}
                  currentPage={page}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={(p) => setPage(p)}
                  pageKey={EMPLOYEE_LIST_KEY}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailPage;